import React from 'react';
import { Select } from 'antd';
import { FN210 } from '../../../services/n211Api';
import { dateToInput, inputToDate, fmt, fmtRate, isFieldDisabledFn, INVOICE_TYPE_OPTIONS, getInvoiceTypeLabel } from '../helpers';
import FieldError from '../../../components/FieldError';

interface Tab1Props {
  formData: Partial<FN210>;
  names: Record<string, string>;
  isEditing: boolean;
  mode: string;
  getField: (key: keyof FN210) => FN210[keyof FN210];
  setField: (key: keyof FN210, value: FN210[keyof FN210]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError: (field: string) => string | undefined;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, mode,
  getField, setField, resolveSingleName, getFieldError,
}) => {
  const dis = (key: string) => isFieldDisabledFn(key, isEditing, mode);

  return (
    <div className="form-grid">
      {/* Row 1: 買收單號 + 交運日期 */}
      <label>買收單號</label>
      <div>
        <input
          value={String(getField('fn210_01') || '')}
          disabled
          className="field-input"
        />
      </div>
      <label>交運日期</label>
      <div>
        <FieldError error={getFieldError('fn210_11')} />
        <input
          type="date"
          value={dateToInput(getField('fn210_11') as string | null)}
          disabled={dis('fn210_11')}
          onChange={e => setField('fn210_11', inputToDate(e.target.value))}
          className="field-input"
        />
      </div>

      {/* Row 2: 廠商代號 + 客訴工單號 */}
      <label>廠商代號</label>
      <div className="lookup-field">
        <FieldError error={getFieldError('fn210_12')} />
        <div className="lookup-row">
          <input
            value={String(getField('fn210_12') || '')}
            disabled={dis('fn210_12')}
            maxLength={12}
            onChange={e => setField('fn210_12', e.target.value)}
            onBlur={() => resolveSingleName('vendor', 'vendor', String(getField('fn210_12') || ''))}
            className="field-input field-code"
          />
          <span className="lookup-name">{names.vendor || ''}</span>
        </div>
      </div>
      <label>客訴工單號</label>
      <div>
        <FieldError error={getFieldError('fn210_28')} />
        <input
          value={String(getField('fn210_28') || '')}
          disabled={dis('fn210_28')}
          maxLength={8}
          onChange={e => setField('fn210_28', e.target.value)}
          className="field-input"
        />
      </div>

      {/* Row 3: 出庫庫位 + 出貨部門代號 */}
      <label>出庫庫位</label>
      <div className="lookup-field">
        <FieldError error={getFieldError('fn210_23')} />
        <div className="lookup-row">
          <input
            value={String(getField('fn210_23') || '')}
            disabled={dis('fn210_23')}
            maxLength={5}
            onChange={e => setField('fn210_23', e.target.value)}
            onBlur={() => resolveSingleName('warehouse', 'warehouse', String(getField('fn210_23') || ''))}
            className="field-input field-code"
          />
          <span className="lookup-name">{names.warehouse || ''}</span>
        </div>
      </div>
      <label>出貨部門代號</label>
      <div className="lookup-field">
        <FieldError error={getFieldError('fn210_16')} />
        <div className="lookup-row">
          <input
            value={String(getField('fn210_16') || '')}
            disabled={dis('fn210_16')}
            maxLength={4}
            onChange={e => setField('fn210_16', e.target.value)}
            onBlur={() => resolveSingleName('department', 'department', String(getField('fn210_16') || ''))}
            className="field-input field-code"
          />
          <span className="lookup-name">{names.department || ''}</span>
        </div>
      </div>

      {/* Row 4: 幣別 + 匯率 */}
      <label>幣別</label>
      <div className="lookup-field">
        <FieldError error={getFieldError('fn210_13')} />
        <div className="lookup-row">
          <input
            value={String(getField('fn210_13') || '')}
            disabled={dis('fn210_13')}
            maxLength={3}
            onChange={e => setField('fn210_13', e.target.value.toUpperCase())}
            onBlur={() => resolveSingleName('currency', 'currency', String(getField('fn210_13') || ''))}
            className="field-input field-code"
          />
          <span className="lookup-name">{names.currency || ''}</span>
        </div>
      </div>
      <label>匯率</label>
      <div>
        <FieldError error={getFieldError('fn210_14')} />
        <input
          value={fmtRate(getField('fn210_14') as number)}
          disabled
          className="field-input"
        />
      </div>

      {/* Row 5: 發票種類 + 計稅 */}
      <label>發票種類</label>
      <div>
        <FieldError error={getFieldError('fn210_251')} />
        {isEditing ? (
          <Select
            value={getField('fn210_251') as number || undefined}
            onChange={(v: number) => setField('fn210_251', v)}
            disabled={dis('fn210_251')}
            style={{ width: '100%' }}
            options={INVOICE_TYPE_OPTIONS}
            placeholder="請選擇"
          />
        ) : (
          <input
            value={getInvoiceTypeLabel(getField('fn210_251') as number)}
            disabled
            className="field-input"
          />
        )}
      </div>
      <label>計稅</label>
      <div>
        <input
          type="checkbox"
          checked={!!getField('fn210_25')}
          disabled={dis('fn210_25')}
          onChange={e => setField('fn210_25', e.target.checked)}
        />
      </div>

      {/* Row 6: 輸入人員 + 出庫通知單號 */}
      <label>輸入人員</label>
      <div className="lookup-field">
        <FieldError error={getFieldError('fn210_15')} />
        <div className="lookup-row">
          <input
            value={String(getField('fn210_15') || '')}
            disabled={dis('fn210_15')}
            onChange={e => setField('fn210_15', e.target.value)}
            onBlur={() => resolveSingleName('employee', 'employee', String(getField('fn210_15') || ''))}
            className="field-input field-code"
          />
          <span className="lookup-name">{names.employee || ''}</span>
        </div>
      </div>
      <label>出庫通知單號</label>
      <div>
        <input
          value={String(getField('fn210_10') || '')}
          disabled
          className="field-input"
        />
      </div>

      {/* Row 7: 備註 (full row) */}
      <label>備註</label>
      <div className="full-row">
        <textarea
          value={String(getField('fn210_17') || '')}
          disabled={dis('fn210_17')}
          onChange={e => setField('fn210_17', e.target.value)}
          className="field-textarea"
          rows={2}
        />
      </div>

      {/* Summary fields (readonly) */}
      <div className="summary-section">
        <div className="summary-row">
          <label>原幣交運金額</label>
          <span className="summary-value">{fmt(formData.fn210_20)}</span>
        </div>
        <div className="summary-row">
          <label>原幣稅額</label>
          <span className="summary-value">{fmt(formData.fn210_21)}</span>
        </div>
        <div className="summary-row">
          <label>原幣交運總額</label>
          <span className="summary-value">{fmt(formData.fn210_201)}</span>
        </div>
        <div className="summary-row">
          <label>本幣交運金額</label>
          <span className="summary-value">{fmt(formData.fn210_22)}</span>
        </div>
        <div className="summary-row">
          <label>發票號碼</label>
          <span className="summary-value">{String(formData.fn210_26 || '-')}</span>
        </div>
        <div className="summary-row">
          <label>總項數</label>
          <span className="summary-value">{formData.fn210_24 || 0}</span>
        </div>
        <div className="summary-row">
          <label>總交運量</label>
          <span className="summary-value">{fmt(formData.fn210_18)}</span>
        </div>
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
